const CURP_REGEX = /\b([A-Z][AEIOUX][A-Z]{2}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[HM][A-Z]{5}[A-Z0-9]\d)\b/gi;
const RFC_REGEX = /\b([A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3})\b/gi;
const EMAIL_REGEX = /\b([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})\b/gi;
const PHONE_REGEX = /(?:(?:\+52\s?)?(?:\(?\d{2,3}\)?[\s.-]?)?\d[\d\s.-]{7,11}\d)/g;

const TYPE_KEYWORDS = {
  ine: [
    "instituto nacional electoral",
    "credencial para votar",
    "vigencia",
    "elector",
  ],
  curp: ["curp", "clave unica de registro de poblacion"],
  acta_nacimiento: ["acta de nacimiento", "registro civil", "oficialia"],
  estado_cuenta: [
    "estado de cuenta",
    "saldo",
    "periodo",
    "movimientos",
    "clabe",
  ],
  comprobante_domicilio: [
    "comprobante de domicilio",
    "cfe",
    "agua",
    "telmex",
    "predial",
  ],
  rfc_constancia: [
    "constancia de situacion fiscal",
    "servicio de administracion tributaria",
    "rfc",
  ],
  nomina: ["recibo de nomina", "percepciones", "deducciones", "nomina"],
};

function normalizeText(text = "") {
  return String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function parsePhoneDigits(candidate = "") {
  const digits = String(candidate || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return digits;
  if (digits.length === 12 && digits.startsWith("52")) return digits.slice(2);
  if (digits.length === 13 && digits.startsWith("521")) return digits.slice(3);
  if (digits.length > 10) return digits.slice(-10);
  return digits;
}

function extractFirst(regex, text = "") {
  const match = regex.exec(text);
  regex.lastIndex = 0;
  return match?.[1] || "";
}

function extractDateISO(text = "") {
  const raw = String(text || "");
  const ddmmyyyy = raw.match(/\b(\d{2})[\/-](\d{2})[\/-](\d{4})\b/);
  if (ddmmyyyy) {
    const [, dd, mm, yyyy] = ddmmyyyy;
    return `${yyyy}-${mm}-${dd}`;
  }
  const yyyymmdd = raw.match(/\b(\d{4})[\/-](\d{2})[\/-](\d{2})\b/);
  if (yyyymmdd) {
    const [, yyyy, mm, dd] = yyyymmdd;
    return `${yyyy}-${mm}-${dd}`;
  }
  return "";
}

function extractAmount(text = "") {
  const matches = String(text || "").match(/\$?\s?\d{1,3}(?:[,.]\d{3})*(?:[,.]\d{2})?/g) || [];
  const candidates = matches
    .map((value) => {
      const normalized = value.replace(/\s|\$/g, "").replace(/,/g, "");
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    })
    .filter((n) => Number.isFinite(n) && n > 0);
  if (!candidates.length) return null;
  return Math.max(...candidates);
}

function detectNameFromLabels(text = "") {
  const lower = normalizeText(text);
  const nameMatch = lower.match(/nombre[s]?\s*[:\-]\s*([a-z\s]{4,80})/i);
  if (!nameMatch) return "";
  return nameMatch[1]
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
    .trim();
}

export function detectDocumentType(text = "", fileMeta = {}) {
  const normalized = normalizeText(text);
  const reasons = [];
  const scoreMap = {
    ine: 0,
    curp: 0,
    acta_nacimiento: 0,
    estado_cuenta: 0,
    comprobante_domicilio: 0,
    rfc_constancia: 0,
    nomina: 0,
    otro: 0.1,
  };

  Object.entries(TYPE_KEYWORDS).forEach(([type, words]) => {
    words.forEach((word) => {
      if (normalized.includes(word)) {
        scoreMap[type] += 0.2;
        reasons.push(`keyword:${type}:${word}`);
      }
    });
  });

  if (CURP_REGEX.test(String(text || ""))) {
    scoreMap.curp += 0.35;
    reasons.push("pattern:curp");
  }
  CURP_REGEX.lastIndex = 0;

  if (RFC_REGEX.test(String(text || ""))) {
    scoreMap.rfc_constancia += 0.25;
    reasons.push("pattern:rfc");
  }
  RFC_REGEX.lastIndex = 0;

  const fileName = normalizeText(fileMeta?.name || "");
  if (fileName.includes("ine")) scoreMap.ine += 0.25;
  if (fileName.includes("curp")) scoreMap.curp += 0.25;
  if (fileName.includes("acta")) scoreMap.acta_nacimiento += 0.25;
  if (fileName.includes("cuenta")) scoreMap.estado_cuenta += 0.25;
  if (fileName.includes("nomina")) scoreMap.nomina += 0.25;
  if (fileName.includes("rfc") || fileName.includes("fiscal")) {
    scoreMap.rfc_constancia += 0.25;
  }

  const ordered = Object.entries(scoreMap).sort((a, b) => b[1] - a[1]);
  const [type, rawScore] = ordered[0] || ["otro", 0.1];
  const confidence = Math.max(0.1, Math.min(0.99, rawScore));
  return { type, confidence, reasons };
}

export function extractStructuredData(text = "", docType = "otro") {
  const raw = String(text || "");
  const upper = raw.toUpperCase();

  const curp = extractFirst(CURP_REGEX, upper);
  const rfc = extractFirst(RFC_REGEX, upper);
  const email = extractFirst(EMAIL_REGEX, raw);
  const phoneMatch = (raw.match(PHONE_REGEX) || [])[0] || "";
  const telefono = parsePhoneDigits(phoneMatch);
  const fechaNacimiento = extractDateISO(raw);
  const nombreCompleto = detectNameFromLabels(raw);
  const monto = ["estado_cuenta", "nomina"].includes(docType)
    ? extractAmount(raw)
    : null;

  return {
    nombres: nombreCompleto,
    fechaNacimiento,
    curp: curp || "",
    rfc: rfc || "",
    email: email || "",
    telefono: telefono || "",
    ingresosMensuales: monto,
    raw_excerpt: raw.slice(0, 6000),
  };
}

export function summarizeExtractedData(extracted = {}) {
  return Object.entries(extracted)
    .filter(([key, value]) => key !== "raw_excerpt" && value !== null && value !== undefined && `${value}`.trim() !== "")
    .map(([key]) => key);
}
